/**
 * Deal model — the commercial view of a lead.
 *
 * SpectraIQ has no separate Deal entity: the sales pipeline IS the Lead
 * Accelerator. A "deal" is a lead that has reached the commercial stages,
 * i.e. once we're quoting a price there is money on the table.
 *
 * This module is the single place that defines what a deal is, what it's
 * worth and how likely it is to close, so the board, the detail page and the
 * forecast tiles can never disagree with each other.
 */
/* No import from ./crm — crm.jsx imports THIS module to derive its deals,
   so a back-import would be circular. */

/** Lead stages that constitute a deal. Order = pipeline order. */
// The commercial tail of the lead pipeline (E1 names).
export const DEAL_STAGES = ['NEGOTIATION', 'CONFIRMED', 'DELIVERY_BOOKED', 'RETAILED', 'DELIVERED', 'LOST'];

/** Board columns. Labels are deal-language, not lead-language. */
export const DEAL_COLUMNS = [
  { key: 'NEGOTIATION',     label: 'Negotiation', color: 'var(--info)' },
  { key: 'CONFIRMED',       label: 'Quoted',      color: 'var(--warning)' },
  { key: 'DELIVERY_BOOKED', label: 'Booked',      color: 'var(--accent-primary)' },
  { key: 'RETAILED',        label: 'Retailed',    color: 'var(--success)' },
  { key: 'DELIVERED',       label: 'Won',         color: 'var(--success)' },
  { key: 'LOST',            label: 'Lost',        color: 'var(--danger)' },
];

/** Close probability by stage — drives the weighted forecast. */
export const PROBABILITY = {
  NEGOTIATION: 25,
  CONFIRMED:   55,
  DELIVERY_BOOKED: 85,
  RETAILED:        95,
  DELIVERED:       100,
  LOST:            0,
};

export const isDealStage = (stage) => DEAL_STAGES.includes(stage);
export const probabilityOf = (stage) => PROBABILITY[stage] ?? 0;
export const isOpenDeal   = (stage) => !['DELIVERED', 'LOST'].includes(stage);
export const dealStageLabel = (stage) =>
  DEAL_COLUMNS.find(c => c.key === stage)?.label || stage;

/**
 * Deal value.
 *
 * We prefer a real driveaway price when the lead carries one. Otherwise we
 * fall back to a configurable average-transaction-price so the forecast isn't
 * silently zero — but we FLAG it (`valueIsEstimated`) so the UI can be honest
 * about which numbers are real and which are assumed. The old board quietly
 * stamped $45k on every card and presented the total as fact.
 */
export const DEFAULT_DEAL_VALUE_AUD = 45000;

/**
 * Deal value, best source first:
 *   1. the driveaway price of the linked stock vehicle  (real)
 *   2. a driveaway price carried on the lead itself     (real)
 *   3. the default average transaction price            (ESTIMATED)
 */
export function dealValue(lead, vehicle) {
  const vehicleCents = vehicle?.driveAwayPriceCents ?? 0;
  if (vehicleCents > 0) return { value: Math.round(vehicleCents / 100), estimated: false };

  const leadCents = lead?.vehicleDriveAwayCents ?? lead?.driveAwayCents ?? 0;
  if (leadCents > 0) return { value: Math.round(leadCents / 100), estimated: false };

  return { value: DEFAULT_DEAL_VALUE_AUD, estimated: true };
}

/** Trade-in is stashed on the lead's discoveryFlags by POST /v1/leads/{id}/trade-in. */
export function tradeInOf(lead) {
  const t = lead?.discoveryFlags?.trade_in;
  return t && typeof t === 'object' ? t : null;
}

/** Build the deal view-model from a lead (+ its customer and linked vehicle). */
export function toDeal(lead, customer, vehicle) {
  const { value, estimated } = dealValue(lead, vehicle);
  const probability = probabilityOf(lead.stage);

  const vehicleTitle = vehicle
    ? [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ')
    : null;

  return {
    id:        lead.id,          // a deal IS a lead — same identity
    leadId:    lead.id,
    stage:     lead.stage,
    title:     vehicleTitle
               || (lead.vehicle && lead.vehicle !== '—' ? lead.vehicle : 'Vehicle TBC'),
    customer,
    customerId: lead.customerId,
    vehicle,
    source:    lead.source,
    simulated: !!lead.simulated,
    score:     lead.score ?? 0,
    value,
    valueIsEstimated: estimated,
    probability,
    weighted:  Math.round(value * probability / 100),
    tradeIn:   tradeInOf(lead),
    vehicleOfInterestId: lead.vehicleOfInterestId,
    assignedConsultantId: lead.assignedConsultantId,
    // Who is replying (AI / HUMAN) — the card's ownership chip reads this.
    assistedBy: lead.assistedBy,
    lostReasonCode: lead.lostReasonCode,
    lostNotes:  lead.lostNotes,
    createdAt:  lead.createdAt,
    stageEnteredAt: lead.stageEnteredAt,
    lastActivityAt: lead.lastActivityAt,
    closedAt:   lead.closedAt,
    notes:      lead.notes,
  };
}

/** Roll a list of deals up into the numbers the board's header shows. */
export function summarise(deals) {
  const open  = deals.filter(d => isOpenDeal(d.stage));
  const won   = deals.filter(d => d.stage === 'DELIVERED');
  const lost  = deals.filter(d => d.stage === 'LOST');

  const sum = (list, key) => list.reduce((acc, d) => acc + (d[key] || 0), 0);

  const wonCount = won.length;
  const decided  = wonCount + lost.length;

  return {
    openCount:  open.length,
    openValue:  sum(open, 'value'),
    weighted:   sum(open, 'weighted'),
    wonValue:   sum(won, 'value'),
    lostValue:  sum(lost, 'value'),
    wonCount,
    lostCount:  lost.length,
    // Win rate by COUNT of decided deals — the old board divided won *value*
    // by won+lost *value*, which is a value-share, not a win rate.
    winRate:    decided ? wonCount / decided : 0,
    anyEstimated: deals.some(d => d.valueIsEstimated),
    // How much of the headline money rests on the default price rather than a
    // real driveaway price — so the totals are never read as confirmed revenue.
    openEstimatedCount: open.filter(d => d.valueIsEstimated).length,
    wonEstimatedCount:  won.filter(d => d.valueIsEstimated).length,
  };
}

export const fmtMoney = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k`;
  return `$${n}`;
};

/** As fmtMoney, with a leading "~" when any of the money is an estimate. */
export const fmtMoneyEst = (n, estimated) => `${estimated ? '~' : ''}${fmtMoney(n)}`;
